import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { GridColDef, GridSortModel, GridRenderCellParams } from "@mui/x-data-grid";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import StyledDataGrid from "../../shared/StyledDataGrid";
import { percentileColorColumn, handlerCodeColumn } from "../../shared/UIParts/columnsFormatter";

export interface ExposureRanges {
  stock: { min: number; max: number };
  currency: { min: number; max: number };
  foreign: { min: number; max: number };
}

interface FundTableProps {
  funds: FundDataBasic[];
  selectedFundId?: number;
  highlightedFundId?: number;
  onSelect?: (fund: FundDataBasic) => void;
  title?: string;
  highlightedColumn?: string;
  initialSortField?: string;
  initialSortDirection?: "asc" | "desc";
  exposureRanges?: ExposureRanges;
}

type RangeStatus = "in" | "near" | "out" | "none";

function getRangeStatus(value: number | null | undefined, min: number, max: number, effectiveMax: number): RangeStatus {
  const v = value ?? 0;
  if (v >= min && v <= effectiveMax) return "in";
  if (v >= min - 10 && v <= effectiveMax + 10) return "near";
  return "out";
}

const RANGE_CELL_COLORS: Record<RangeStatus, string | undefined> = {
  in: "#c8e6c9",
  near: "#ffe0b2",
  out: "#ffcdd2",
  none: undefined,
};

function exposureCell(
  field: keyof FundDataBasic,
  headerName: string,
  width: number,
  getRange: (ranges: ExposureRanges) => { min: number; max: number },
  exposureRanges: ExposureRanges | undefined
): GridColDef {
  return {
    field: field as string,
    headerName,
    width,
    renderCell: (params: GridRenderCellParams) => {
      const raw = params.value as number | null | undefined;
      const display = raw != null ? `${raw.toFixed(1)}%` : "—";

      let bg: string | undefined;
      if (exposureRanges != null) {
        const range = getRange(exposureRanges);
        const effectiveMax = range.max === 100 ? 120 : range.max;
        const status = getRangeStatus(raw, range.min, range.max, effectiveMax);
        bg = RANGE_CELL_COLORS[status];
      }

      return (
        <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: bg }}>
          {display}
        </Box>
      );
    },
  };
}

const FundTable: React.FC<FundTableProps> = ({
  funds,
  selectedFundId,
  highlightedFundId,
  onSelect,
  title,
  highlightedColumn,
  initialSortField = "cumulative_12m_yield",
  initialSortDirection = "desc",
  exposureRanges,
}) => {

  const fundsWithHandlerCode = useMemo(() => {
    return funds.map(fund => ({
      ...fund,
      handler_code: String(fund.managing_corporation_legal_id || ""),
    }));
  }, [funds]);

  const initialSortModel: GridSortModel = useMemo(() => [
    { field: initialSortField, sort: initialSortDirection },
  ], [initialSortField, initialSortDirection]);

  const columns: GridColDef[] = useMemo(() => {
    const percentageCol = (field: string, headerName: string, width: number): GridColDef => ({
      field,
      headerName,
      width,
      renderCell: (params: GridRenderCellParams) => {
        const raw = params.value as number | null | undefined;
        return raw != null ? `${raw.toFixed(1)}%` : "—";
      },
    });

    return [
      { field: "fund_id", headerName: "מס׳ מסלול", width: 70 },
      handlerCodeColumn("handler_code", "יצרן"),
      { field: "fund_name", headerName: "שם מסלול", width: 180 },
      { field: "sub_specialization", headerName: "תת התמחות", width: 100 },
      percentileColorColumn("percentile_6m_yield", "דירוג 6M"),
      percentageCol("cumulative_6m_yield", "תשואה 6 חודשים", 80),
      percentageCol("year_to_date_yield", "תשואה YTD", 80),
      percentageCol("cumulative_12m_yield", "תשואה 12 חודשים", 80),
      percentageCol("yield_trailing_3_yrs", "תשואה 3 שנים", 80),
      percentageCol("yield_trailing_5_yrs", "תשואה 5 שנים", 80),
      exposureCell("stock_market_percent", "חשיפה למניות", 80, r => r.stock, exposureRanges),
      exposureCell("foreign_currency_exposure_percent", "חשיפה למט\"ח", 80, r => r.currency, exposureRanges),
      exposureCell("foreign_exposure_percent", "חשיפה לחו\"ל", 80, r => r.foreign, exposureRanges),
    ].map(col => ({
      ...col,
      cellClassName: highlightedColumn === col.field ? "highlighted-column" : undefined,
    }));
  }, [exposureRanges, highlightedColumn]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <StyledDataGrid
        rows={fundsWithHandlerCode}
        columns={columns}
        initialState={{
          sorting: { sortModel: initialSortModel },
        }}
        onRowClick={(params) => {
          const fund = funds.find(f => f.uid === params.id || f.fund_id === params.row.fund_id);
          if (fund && onSelect) onSelect(fund);
        }}
        getRowClassName={(params) => {
          const fundId = params.row.fund_id;
          if (fundId === selectedFundId) return "li-selected";
          if (fundId === highlightedFundId) return "li-highlighted";
          return "";
        }}
        sx={{
          '& .li-selected': {
            backgroundColor: '#fffbe6 !important',
            border: '1px dashed orange !important',
          },
          '& .li-highlighted': {
            backgroundColor: '#e3f2fd !important',
          },
          '& .highlighted-column': {
            backgroundColor: '#e0f7fa !important',
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        maxHeight='700px'
      />
    </Box>
  );
};

export default FundTable;
